import { useEffect, useMemo } from 'react';
import { useBrand } from '../../contexts/BrandContext';
import { sanitizeText, sanitizeUrl } from '../../utils/sanitize';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  noIndex?: boolean;
}

export const SEOHead: React.FC<SEOHeadProps> = ({ 
  title, 
  description, 
  keywords,
  canonical,
  noIndex = false
}) => {
  const { brand } = useBrand();

  // Memoize computed values to prevent recalculation
  const pageTitle = useMemo(() => {
    const sanitizedTitle = sanitizeText(title || '');
    const sanitizedBrandName = sanitizeText(brand.brandName || 'Ghana Family System');
    const sanitizedTagline = sanitizeText(brand.brandTagline || '');
    
    return sanitizedTitle 
      ? sanitizeText(`${sanitizedTitle} | ${sanitizedBrandName}`) 
      : sanitizeText(`${sanitizedBrandName}${sanitizedTagline ? ` - ${sanitizedTagline}` : ''}`);
  }, [title, brand.brandName, brand.brandTagline]);

  const metaDescription = useMemo(() => {
    return sanitizeText(description || brand.brandDescription || 'Ghana family membership management system');
  }, [description, brand.brandDescription]);

  const metaKeywords = useMemo(() => {
    if (keywords && typeof keywords === 'string') return sanitizeText(keywords);
    const sanitizedBrandName = sanitizeText(brand.brandName || 'Ghana Family System');
    return sanitizeText([sanitizedBrandName, 'Ghana family membership', 'family management system', 'mobile money', 'MoMo payments', 'family contributions', 'Ghana'].join(', '));
  }, [keywords, brand.brandName]);

  useEffect(() => {
    // Update document title
    document.title = sanitizeText(pageTitle);

    // Update meta description
    let descMeta = document.querySelector('meta[name="description"]');
    if (!descMeta) {
      descMeta = document.createElement('meta');
      descMeta.setAttribute('name', 'description');
      document.head.appendChild(descMeta);
    }
    descMeta.setAttribute('content', sanitizeText(metaDescription));

    // Update meta keywords
    let keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (!keywordsMeta) {
      keywordsMeta = document.createElement('meta');
      keywordsMeta.setAttribute('name', 'keywords');
      document.head.appendChild(keywordsMeta);
    }
    keywordsMeta.setAttribute('content', metaKeywords);

    // Update favicon using proper favicon logic
    const faviconUrl = brand.brandFavicon || brand.brandLogo || '/favicon.ico';
    
    // Remove existing favicons first
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(f => f.remove());
    
    // Add new favicon
    const favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/x-icon';
    favicon.href = faviconUrl;
    document.head.appendChild(favicon);
    
    // Add apple-touch-icon
    const appleTouchIcon = document.createElement('link');
    appleTouchIcon.rel = 'apple-touch-icon';
    appleTouchIcon.href = faviconUrl;
    document.head.appendChild(appleTouchIcon);

    // Update theme color
    let themeColor = document.querySelector('meta[name="theme-color"]');
    if (!themeColor) {
      themeColor = document.createElement('meta');
      themeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(themeColor);
    }
    themeColor.setAttribute('content', brand.brandColors.primary);

    // Update robots meta
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      const sanitizedCanonical = sanitizeUrl(canonical);
      if (sanitizedCanonical) {
        if (!canonicalLink) {
          canonicalLink = document.createElement('link');
          canonicalLink.setAttribute('rel', 'canonical');
          document.head.appendChild(canonicalLink);
        }
        canonicalLink.setAttribute('href', sanitizedCanonical);
      }
    } else if (canonicalLink) {
      canonicalLink.remove();
    }

    // Update Open Graph tags
    const ogImageUrl = sanitizeUrl(brand.brandLogo || '/logo.png');
    const currentUrl = sanitizeUrl(canonical || window.location.href);
    const ogTags = [
      { property: 'og:title', content: pageTitle },
      { property: 'og:description', content: metaDescription },
      { property: 'og:image', content: ogImageUrl },
      { property: 'og:url', content: currentUrl },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: sanitizeText(brand.brandName || 'Ghana Family System') },
      { property: 'og:locale', content: 'en_GH' }
    ];

    ogTags.forEach(({ property, content }) => {
      let ogMeta = document.querySelector(`meta[property="${property}"]`);
      if (!ogMeta) {
        ogMeta = document.createElement('meta');
        ogMeta.setAttribute('property', property);
        document.head.appendChild(ogMeta);
      }
      ogMeta.setAttribute('content', content);
    });

    // Update Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageTitle },
      { name: 'twitter:description', content: metaDescription },
      { name: 'twitter:image', content: ogImageUrl }
    ];

    twitterTags.forEach(({ name, content }) => {
      let twitterMeta = document.querySelector(`meta[name="${name}"]`);
      if (!twitterMeta) {
        twitterMeta = document.createElement('meta');
        twitterMeta.setAttribute('name', name);
        document.head.appendChild(twitterMeta);
      }
      twitterMeta.setAttribute('content', content);
    });

    // Update JSON-LD structured data
    let jsonLdScript = document.querySelector('script[type="application/ld+json"]');
    if (!jsonLdScript) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(jsonLdScript);
    }
    
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: sanitizeText(brand.brandName || 'Ghana Family System'),
      description: sanitizeText(brand.brandDescription || 'Ghana family membership management system'),
      url: currentUrl,
      logo: ogImageUrl,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        areaServed: 'GH'
      }
    };
    
    jsonLdScript.textContent = JSON.stringify(structuredData);

  }, [pageTitle, metaDescription, metaKeywords, canonical, noIndex, brand.brandColors.primary, brand.brandLogo]);

  return null;
};